import { useState } from 'react';
import { Box, Flex, InputProps, ButtonProps } from '@chakra-ui/react';
import { Formik, FormikErrors } from 'formik';
import { FaUnlockAlt, FaUserCircle } from 'react-icons/fa';
import { useSignIn } from 'hooks';
import { useSession } from 'contexts/sessionContext';
import { Button, Input, FormLabel, FormControl, Icon, Alert, AlertDescription } from 'components/UI';
import { validateEmail, validateRequired } from 'utils/validation';
import { validateForm } from 'utils/helpers';
import { errorMessageEmailInvalid } from 'constants/validation';
import { ISignInRequest } from '../../../../server/types';

type LoginFormProps = {
  overlay: boolean;
  inputProps?: InputProps;
  buttonProps?: ButtonProps;
};

export type LoginFormValues = {
  email: string;
  password: string;
};

const LoginForm = ({ overlay, inputProps = {}, buttonProps = {} }: LoginFormProps) => {
  const [signIn, { error: loginError }] = useSignIn();
  const [sessionError, setSessionError] = useSession();
  const [submitted, setSubmitted] = useState(false);

  const handleLogin = async ({ email, password }: ISignInRequest) => {
    await signIn(email, password);
    setSessionError(undefined);
  };

  const error = loginError || sessionError;

  const renderError = error ? (
    <Alert variant="subtle" bgColor="transparent" justifyContent="center">
      <AlertDescription>{error}</AlertDescription>
    </Alert>
  ) : null;

  const renderPasswordIcon = !overlay ? <Icon as={FaUnlockAlt} size="lg" mr={6} data-testid="unlock-icon" /> : null;

  const renderEmailIcon = !overlay ? <Icon as={FaUserCircle} size="lg" mr={6} data-testid="email-icon" /> : null;

  return (
    <Flex flex={1}>
      <Formik
        validate={(values: ISignInRequest) => {
          const errors: FormikErrors<ISignInRequest> = validateRequired<ISignInRequest>(['email', 'password'], values);

          if (values.email && !validateEmail(values.email)) {
            errors.email = errorMessageEmailInvalid;
          }

          return errors;
        }}
        initialValues={{ email: '', password: '' }}
        onSubmit={handleLogin}
      >
        {(formConfig) => {
          const { values, handleChange, handleBlur, handleSubmit, isSubmitting } = formConfig;

          const formErrors = validateForm<LoginFormValues>({
            ...formConfig,
            submitted,
          });

          return (
            <form style={{ width: '100%' }} onSubmit={handleSubmit}>
              <Flex flex={1} mb={6}>
                {renderEmailIcon}
                <Box flex={1}>
                  <FormControl isInvalid={!!formErrors.email}>
                    <FormLabel htmlFor="email">EMAIL</FormLabel>
                    <Input
                      type="email"
                      name="email"
                      maxLength={254}
                      value={values.email}
                      error={formErrors.email}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      placeholder="Enter your email"
                      {...inputProps}
                    />
                  </FormControl>
                </Box>
              </Flex>

              <Flex flex={1} mb={6}>
                {renderPasswordIcon}
                <Box flex={1}>
                  <FormControl isInvalid={!!formErrors.password}>
                    <FormLabel htmlFor="password">PASSWORD</FormLabel>
                    <Input
                      type="password"
                      name="password"
                      maxLength={30}
                      value={values.password}
                      error={formErrors.password}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      placeholder="Enter your password"
                      {...inputProps}
                    />
                  </FormControl>
                </Box>
              </Flex>
              <Button
                isDisabled={isSubmitting}
                isLoading={isSubmitting}
                variant="solid"
                size="lg"
                type="submit"
                width="100%"
                onClick={() => setSubmitted(true)}
                {...buttonProps}
              >
                LOGIN
              </Button>

              {renderError}
            </form>
          );
        }}
      </Formik>
    </Flex>
  );
};

export default LoginForm;
